import { Link } from "@tanstack/react-router";
import { useClerk } from "@clerk/clerk-react";
import { ApiError } from "@plotlounge/shared/api";
import styles from "./ClubsEmptyState.module.scss";

type Reason = "not-organizer" | "not-organizer-of-club" | "error";

interface Props {
  reason: Reason;
  error?: unknown;
  onRetry?: () => void;
}

const COPY: Record<Reason, { title: string; body: string }> = {
  "not-organizer": {
    title: "No clubs to run yet",
    body: "The cockpit is for owners and admins of book clubs. Once you start or take over a club, it'll show up here.",
  },
  "not-organizer-of-club": {
    title: "Not your club",
    body: "You're not an owner or admin of this club, so the cockpit can't open it.",
  },
  error: {
    title: "Something went wrong",
    body: "We couldn't load your clubs. Try again, or sign out and back in if it keeps failing.",
  },
};

function describeError(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof ApiError) {
    return `${error.status} — ${error.message}`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return null;
}

export function ClubsEmptyState({ reason, error, onRetry }: Props) {
  const copy = COPY[reason];
  const detail = reason === "error" ? describeError(error) : null;
  const { signOut } = useClerk();

  return (
    <main className={styles.root}>
      <h1 className={styles.title}>{copy.title}</h1>
      <p className={styles.body}>{copy.body}</p>
      {detail && <p className={styles.detail}>{detail}</p>}
      <div className={styles.actions}>
        {reason === "error" ? (
          <>
            <button
              type="button"
              className={styles.button}
              onClick={() => {
                if (onRetry) onRetry();
                else window.location.reload();
              }}
            >
              Try again
            </button>
            <button
              type="button"
              className={styles.link}
              onClick={() => void signOut({ redirectUrl: "/sign-in" })}
            >
              Sign out
            </button>
          </>
        ) : (
          <Link to="/" className={styles.link}>
            Back to home
          </Link>
        )}
      </div>
    </main>
  );
}
